import React, { useState, useEffect, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Formik,
  Form,
  Field,
  yupToFormErrors,
  setNestedObjectValues
} from 'formik';
import cx from 'classnames';
import Input from '@hobs/components/Input';
import Money from '@hobs/components/Money';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import Dropdown from '@hobs/components/Dropdown';
import Dialog from '@hobs/components/Dialog';
import DropZone from '@hobs/components/DropZone';
import Switch from '@hobs/components/Switch';
import * as Yup from 'yup';
import Confirm from '@hobs/components/Confirm';
import { withRouter } from 'react-router-dom';
import ShortId from 'shortid';
import Quantity from '@hobs/components/Quantity';

import './AssignmentResponse.scss';

const AssignmentResponse = ({
  delivery = {},
  assignmentResponse,
  t,
  onSubmit,
  vatOptions,
  getSelectedVat,
  disabled,
  isFileDialogOpen,
  onFileDialogClose,
  onFileSelect,
  onFileDialogOpen,
  isDeliveryFileDialogOpen,
  onDeliveryFileDialogOpen,
  onDeliveryFileDialogClose,
  onDeliveryFinalizedChange,
  onDeliverySubmit,
  onDeliveryRollback
}) => {
  const [key, setKey] = useState(ShortId.generate());
  const [
    deliveryQuantityApprovalRequired,
    setDeliveryQuantityApprovalRequired
  ] = useState(false);

  // https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const prevDelivery = usePrevious(delivery);

  useEffect(() => {
    if (JSON.stringify(delivery) !== JSON.stringify(prevDelivery)) {
      onRollBackUpdate();
    }
  });

  const onRollBackUpdate = () => {
    setKey(ShortId.generate());
  };

  const onFormSubmit = (values, actions) => {
    if (values.SUBMIT_TYPE === 'RESPONSE') {
      Yup.object()
        .shape({
          canDeliveryQuantity: Yup.number()
            .required(t('Quantity is required'))
            .typeError('Invalid number')
            .min(0, t('Quantity must be greater than 0'))
            .nullable(),
          comment: Yup.string()
            .max(1500, t('Comment cannot be longer than 500 symbols'))
            .nullable(),
          unitCostDisplay: Yup.number()
            .min(0, t('Price must be greater than 0'))
            .nullable(),
          totalCostDisplay: Yup.number()
            .min(0, t('Total must be greater than 0'))
            .nullable()
        })
        .validate(values, { abortEarly: false })
        .catch(errors => {
          actions.setErrors(yupToFormErrors(errors));
          actions.setTouched(setNestedObjectValues(values, true));
          actions.setSubmitting(false);
        })
        .then(response => {
          if (response) {
            onSubmit(values, actions);
          }
        });
    }

    if (values.SUBMIT_TYPE === 'DELIVERY') {
      Yup.object()
        .shape({
          delivery: Yup.object().shape({
            deliveryQuantity: Yup.number()
              .nullable()
              .required(t('Quantity is required'))
              .typeError('Quantity is required')
              .positive(t('Quantity must be greater than 0')),
            unitCostDisplay: Yup.number()
              .required(t('Price is required'))
              .typeError('Price is required')
              .min(0, t('Price must be equal to or greater than 0'))
              .nullable(),
            totalCostDisplay: Yup.number()
              .required(t('Total cost is required'))
              .typeError('Total cost is required')
              .min(0, t('Total cost must be equal to or greater than 0'))
              .nullable(),
            valueAddedTaxId: Yup.string()
              .required(t('VAT is required'))
              .nullable(),
            comment: Yup.string()
              .max(1500, t('Comment cannot be longer than 500 symbols'))
              .nullable()
          })
        })
        .validate(values, { abortEarly: false })
        .catch(errors => {
          actions.setErrors(yupToFormErrors(errors));
          actions.setTouched(setNestedObjectValues(values, true));
          actions.setSubmitting(false);
        })
        .then(response => {
          if (response) {
            if (!isQuantityApprovalRequired(values)) {
              onDeliverySubmit(values.delivery, actions);
            } else {
              setDeliveryQuantityApprovalRequired(true);
            }
          }
        });
    }
  };

  function isQuantityApprovalRequired(values) {
    return values.canDeliveryQuantity < values.delivery.deliveryQuantity;
  }

  function getDefaultVat() {
    return getSelectedVat ? getSelectedVat().valueAddedTaxId : null;
  }

  function getResponseValues(assignmentResponse) {
    return {
      ...assignmentResponse,
      delivery: {
        ...delivery,
        attachments: delivery.attachments ? delivery.attachments : [],
        unitCostDisplay: delivery.unitCost
          ? delivery.unitCost.toFixed(2)
          : delivery.unitCost,
        totalCostDisplay: delivery.totalCost
          ? delivery.totalCost.toFixed(2)
          : delivery.totalCost
      },
      totalCostDisplay: assignmentResponse.totalCost,
      unitCostDisplay: assignmentResponse.unitCost,
      valueAddedTaxId: assignmentResponse.valueAddedTaxId
        ? assignmentResponse.valueAddedTaxId
        : getDefaultVat()
    };
  }

  function calcUnitCost(canDeliveryQuantity, totalCost, setFieldValue) {
    if (!canDeliveryQuantity || !totalCost) {
      return;
    }

    setFieldValue(
      'unitCostDisplay',
      (totalCost / canDeliveryQuantity).toFixed(2)
    );
    setFieldValue('totalCost', totalCost);
    setFieldValue('unitCost', totalCost / canDeliveryQuantity);
  }

  function calcTotalCost(canDeliveryQuantity, unitCost, setFieldValue) {
    if ((!canDeliveryQuantity && canDeliveryQuantity !== 0) || !unitCost) {
      return;
    }

    setFieldValue(
      'totalCostDisplay',
      (unitCost * canDeliveryQuantity).toFixed(2)
    );
    setFieldValue('totalCost', unitCost * canDeliveryQuantity);
    setFieldValue('unitCost', unitCost);
  }

  function calcDeliveryUnitCost(canDeliveryQuantity, totalCost, setFieldValue) {
    setFieldValue('delivery.totalCost', totalCost);

    if (!canDeliveryQuantity) {
      return;
    }
    if (!totalCost && totalCost !== 0) {
      totalCost = 0;
    }

    setFieldValue(
      'delivery.unitCostDisplay',
      (totalCost / canDeliveryQuantity).toFixed(2)
    );
    setFieldValue('delivery.unitCost', totalCost / canDeliveryQuantity);
  }

  function calcDeliveryTotalCost(canDeliveryQuantity, unitCost, setFieldValue) {
    setFieldValue('delivery.unitCost', unitCost);

    if (!unitCost && unitCost !== 0) {
      unitCost = 0;
    }
    if (!canDeliveryQuantity && canDeliveryQuantity !== 0) {
      canDeliveryQuantity = 0;
    }

    setFieldValue(
      'delivery.totalCostDisplay',
      (unitCost * canDeliveryQuantity).toFixed(2)
    );
    setFieldValue('delivery.totalCost', unitCost * canDeliveryQuantity);
  }

  const initialValues = assignmentResponse
    ? getResponseValues(assignmentResponse)
    : {
        canDeliveryQuantity: '',
        unitCostDisplay: '',
        unitCost: '',
        totalCost: '',
        totalCostDisplay: '',
        grossAmount: '',
        comment: '',
        isDraft: true,
        valueAddedTaxId: getDefaultVat(),
        attachments: [],
        delivery: {
          ...delivery,
          attachments: delivery.attachments ? delivery.attachments : [],
          unitCostDisplay: delivery.unitCost,
          totalCostDisplay: delivery.totalCost
        }
      };

  const isControlsVisible = () => {
    return (
      (!disabled &&
        assignmentResponse &&
        (assignmentResponse.isDraft === null ||
          assignmentResponse.isDraft === true)) ||
      (!disabled && assignmentResponse && assignmentResponse.canBeEdited)
    );
  };

  return (
    <div className="assignment-response">
      <Formik
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={onFormSubmit}
        key={key}
      >
        {({
          handleSubmit,
          setFieldValue,
          values,
          isSubmitting,
          setSubmitting
        }) => (
          <Form noValidate={true}>
            <Dialog
              title={t('Response files')}
              isOpen={isFileDialogOpen}
              onClose={onFileDialogClose}
              className="response-files"
            >
              <DropZone
                onSelect={files => {
                  onFileSelect(files).then(response => {
                    setFieldValue(
                      'attachments',
                      values.attachments.concat(response.payload)
                    );
                  });
                }}
                onRemove={(file, list) => {
                  setFieldValue('attachments', list);
                }}
                value={values.attachments ? values.attachments : []}
                key={ShortId.generate()}
                disabled={
                  disabled ||
                  (assignmentResponse && !assignmentResponse.canBeEdited)
                }
                showSuccessMessages={true}
              />
            </Dialog>
            <Dialog
              title={t('Delivery files')}
              isOpen={isDeliveryFileDialogOpen}
              onClose={onDeliveryFileDialogClose}
              className="response-files"
            >
              <DropZone
                onSelect={files => {
                  onFileSelect(files).then(response => {
                    setFieldValue(
                      'delivery.attachments',
                      values.delivery.attachments.concat(response.payload)
                    );
                  });
                }}
                onRemove={(file, list) => {
                  setFieldValue('delivery.attachments', list);
                }}
                value={
                  values.delivery.attachments ? values.delivery.attachments : []
                }
                key={ShortId.generate()}
                disabled={!values.delivery.isEditable}
                showSuccessMessages={true}
              />
            </Dialog>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('Delivery')}</th>
                  <th>{t('Price')}</th>
                  <th>{t('Total')}</th>
                  <th>{t('VAT')}</th>
                  <th>{t('Gross amount')}</th>
                  <th>{t('Comment')}</th>
                  <th>{t('Files')}</th>
                  <th />
                  {isControlsVisible() && <th />}
                </tr>
              </thead>
              <tbody>
                {assignmentResponse && (
                  <tr>
                    <td className="small">
                      {(disabled || !assignmentResponse.canBeEdited) && (
                        <Quantity amount={values.canDeliveryQuantity} />
                      )}
                      {!disabled && assignmentResponse.canBeEdited && (
                        <Field
                          name="canDeliveryQuantity"
                          component={Input}
                          noMargin
                          setFieldValue={setFieldValue}
                          min={0}
                          type="number"
                          onChange={event => {
                            calcTotalCost(
                              event.target.value,
                              values.unitCost,
                              setFieldValue
                            );
                          }}
                        />
                      )}
                    </td>
                    <td className="small">
                      {(disabled || !assignmentResponse.canBeEdited) && (
                        <Money amount={values.unitCost} />
                      )}
                      {!disabled && assignmentResponse.canBeEdited && (
                        <>
                          <Field
                            name="unitCostDisplay"
                            component={Input}
                            noMargin
                            min={0}
                            setFieldValue={setFieldValue}
                            type="number"
                            onChange={event => {
                              calcTotalCost(
                                values.canDeliveryQuantity,
                                event.target.value,
                                setFieldValue
                              );
                            }}
                          />
                          <Field
                            name="unitCost"
                            min={0}
                            setFieldValue={setFieldValue}
                            type="hidden"
                          />
                        </>
                      )}
                    </td>
                    <td className="small">
                      {(disabled || !assignmentResponse.canBeEdited) && (
                        <Money amount={values.totalCost} />
                      )}
                      {!disabled && assignmentResponse.canBeEdited && (
                        <>
                          <Field
                            name="totalCostDisplay"
                            component={Input}
                            noMargin
                            min={0}
                            type="number"
                            setFieldValue={setFieldValue}
                            onChange={event => {
                              calcUnitCost(
                                values.canDeliveryQuantity,
                                event.target.value,
                                setFieldValue
                              );
                            }}
                          />
                          <Field name="totalCost" min={0} type="hidden" />
                        </>
                      )}
                    </td>

                    <td className="medium">
                      {(disabled || !assignmentResponse.canBeEdited) && (
                        <span>{assignmentResponse.vatValue}%</span>
                      )}
                      {!disabled && assignmentResponse.canBeEdited && (
                        <Field
                          setFieldValue={setFieldValue}
                          name="valueAddedTaxId"
                          component={Dropdown}
                          disabled={
                            !values.canDeliveryQuantity ||
                            !values.unitCost ||
                            !values.totalCost
                          }
                        >
                          {vatOptions}
                        </Field>
                      )}
                    </td>
                    <td className="small">
                      {(disabled || !assignmentResponse.canBeEdited) && (
                        <Money amount={values.grossAmount} />
                      )}
                      {!disabled && assignmentResponse.canBeEdited && (
                        <Money
                          amount={
                            values.totalCost *
                            (getSelectedVat(values.valueAddedTaxId).percentage /
                              100 +
                              1)
                          }
                        />
                      )}
                    </td>
                    <td className="comment">
                      {(disabled || !assignmentResponse.canBeEdited) && (
                        <span>{values.comment}</span>
                      )}
                      {!disabled && assignmentResponse.canBeEdited && (
                        <Field
                          name="comment"
                          component={Input}
                          noMargin
                          setFieldValue={setFieldValue}
                        />
                      )}
                    </td>
                    <td className="small">
                      <div
                        onClick={onFileDialogOpen}
                        className="response-attachment-link"
                      >
                        <Icon
                          icon="attachment"
                          className={cx('files-icon', {
                            'no-files': !values.attachments.length
                          })}
                        />
                        {values.attachments.length
                          ? `(${values.attachments.length})`
                          : ''}
                      </div>
                    </td>
                    <td />
                    {isControlsVisible() && (
                      <td className="controls">
                        <>
                          {(assignmentResponse.isDraft === null ||
                            assignmentResponse.isDraft === true) && (
                            <GhostButton
                              onClick={async () => {
                                await setFieldValue('SUBMIT_TYPE', 'RESPONSE');
                                await setFieldValue('isDraft', true);
                                handleSubmit();
                              }}
                              type="button"
                              disabled={isSubmitting}
                            >
                              {t('Save draft')}
                            </GhostButton>
                          )}
                          {assignmentResponse.canBeEdited && (
                            <RaisedButton
                              onClick={async () => {
                                await setFieldValue('SUBMIT_TYPE', 'RESPONSE');
                                await setFieldValue('isDraft', false);
                                handleSubmit();
                              }}
                              type="button"
                              disabled={
                                isSubmitting || !assignmentResponse.canBeEdited
                              }
                            >
                              {t('Send response')}
                            </RaisedButton>
                          )}
                        </>
                      </td>
                    )}
                  </tr>
                )}
                {values.delivery.isVisible && (
                  <tr>
                    <td>
                      <Field
                        noMargin
                        disabled={!values.delivery.isEditable}
                        component={Input}
                        type="number"
                        name="delivery.deliveryQuantity"
                        onChange={event => {
                          calcDeliveryTotalCost(
                            event.target.value,
                            values.delivery.unitCost,
                            setFieldValue
                          );
                        }}
                      />
                    </td>
                    <td>
                      <Field
                        name="delivery.unitCostDisplay"
                        component={Input}
                        noMargin
                        min={0}
                        disabled={!values.delivery.isEditable}
                        setFieldValue={setFieldValue}
                        type="number"
                        onChange={event => {
                          calcDeliveryTotalCost(
                            values.delivery.deliveryQuantity,
                            event.target.value,
                            setFieldValue
                          );
                        }}
                      />
                      <Field
                        name="unitCost"
                        noMargin
                        min={0}
                        setFieldValue={setFieldValue}
                        type="hidden"
                      />
                    </td>
                    <td>
                      <Field
                        name="delivery.totalCostDisplay"
                        component={Input}
                        noMargin
                        min={0}
                        type="number"
                        disabled={!values.delivery.isEditable}
                        setFieldValue={setFieldValue}
                        onChange={event => {
                          calcDeliveryUnitCost(
                            values.delivery.deliveryQuantity,
                            event.target.value,
                            setFieldValue
                          );
                        }}
                      />
                      <Field
                        name="delivery.totalCost"
                        noMargin
                        min={0}
                        setFieldValue={setFieldValue}
                        type="hidden"
                      />
                    </td>
                    <td>
                      <Field
                        noMargin
                        setFieldValue={setFieldValue}
                        component={Dropdown}
                        disabled={!values.delivery.isEditable}
                        name="delivery.valueAddedTaxId"
                      >
                        <option value="">{t('Select VAT')}</option>
                        {vatOptions}
                      </Field>
                    </td>
                    <td>
                      <Money
                        amount={
                          values.delivery.totalCost *
                          (getSelectedVat(values.delivery.valueAddedTaxId)
                            .percentage /
                            100 +
                            1)
                        }
                      />
                    </td>
                    <td>
                      <Field
                        noMargin
                        disabled={!values.delivery.isEditable}
                        component={Input}
                        name="delivery.comment"
                      />
                    </td>
                    <td className="small">
                      <div
                        onClick={onDeliveryFileDialogOpen}
                        className="response-attachment-link"
                      >
                        <Icon
                          icon="attachment"
                          className={cx('files-icon', {
                            'no-files': !values.delivery.attachments.length
                          })}
                        />
                        {values.delivery.attachments.length
                          ? `(${values.delivery.attachments.length})`
                          : ''}
                      </div>
                    </td>
                    <td>
                      <Switch
                        disabled={!values.delivery.isEditable}
                        checked={values.delivery.isDeliveryFinalized}
                        onChange={deliveryFinalized => {
                          Confirm(
                            t('Change delivery finalized status'),
                            deliveryFinalized
                              ? t(
                                  'Are you sure you want confirm delivery as finalized?'
                                )
                              : t(
                                  'Are you sure you want reset delivery as not finalized?'
                                ),
                            () => {
                              onDeliveryFinalizedChange(
                                deliveryFinalized,
                                () => {
                                  setFieldValue(
                                    'delivery.isDeliveryFinalized',
                                    deliveryFinalized
                                  );
                                }
                              );
                            }
                          );
                        }}
                        label={t('Delivery finalized')}
                      />
                    </td>
                    <td className="controls">
                      <GhostButton
                        disabled={isSubmitting || !values.delivery.isEditable}
                        onClick={async () => {
                          await setFieldValue('SUBMIT_TYPE', 'DELIVERY');
                          handleSubmit();
                        }}
                        type="button"
                      >
                        {t('Confirm delivery')}
                      </GhostButton>
                      {deliveryQuantityApprovalRequired &&
                        Confirm(
                          t('Delivery Quantity'),
                          t(
                            'Delivery quantity is greater than was responded. Are you sure, you want to confirm?'
                          ),
                          async () => {
                            setDeliveryQuantityApprovalRequired(false);
                            onDeliverySubmit(values.delivery, {
                              setSubmitting
                            });
                          },
                          () => {
                            setDeliveryQuantityApprovalRequired(false);
                            setSubmitting(false);
                          }
                        )}
                      {delivery.canRollBack && (
                        <GhostButton
                          disabled={isSubmitting}
                          onClick={() => {
                            Confirm(
                              t('Rollback'),
                              t(
                                'Are you sure you want to change service request status back to "Confirmed"?'
                              ),
                              () => {
                                onDeliveryRollback();
                              }
                            );
                          }}
                          type="button"
                        >
                          {t('Rollback')}
                        </GhostButton>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Form>
        )}
      </Formik>
    </div>
  );
};

AssignmentResponse.propTypes = {
  delivery: PropTypes.object,
  assignmentResponse: PropTypes.object,
  t: PropTypes.func,
  onSubmit: PropTypes.func,
  vatOptions: PropTypes.array,
  getSelectedVat: PropTypes.func,
  disabled: PropTypes.bool,
  isFileDialogOpen: PropTypes.bool,
  responseFilesDialogUrl: PropTypes.string,
  onFileDialogClose: PropTypes.func,
  onFileSelect: PropTypes.func,
  onFileDialogOpen: PropTypes.func,
  isDeliveryFileDialogOpen: PropTypes.bool,
  onDeliveryFileDialogClose: PropTypes.func,
  onDeliveryFileDialogOpen: PropTypes.func,
  onDeliveryFinalizedChange: PropTypes.func,
  onDeliverySubmit: PropTypes.func,
  onDeliveryRollback: PropTypes.func
};

export default withRouter(withTranslation()(AssignmentResponse));
